// ManagementTable.jsx
import React, { useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { MdEdit } from "react-icons/md";
import { FiSearch } from "react-icons/fi";
import Card from "components/card"; 
import EditUserModal from "../components/functions/EditUserModal";

const ManagementTable = ({ columnsData = [], tableData = [] }) => {
  const [editUser, setEditUser] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [schoolFilter, setSchoolFilter] = useState('all');

  /* ────────────────────── React‑Table setup - HOOKS MUST BE CALLED UNCONDITIONALLY ────────────────────── */
  // These useMemo and useTable calls must always happen at the top level.
  // They are now safe because columnsData and tableData default to empty arrays.
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);

  // Filter data based on selected filters
  const filteredData = useMemo(() => {
    return data.filter(user => {
      const roleMatch = roleFilter === 'all' || user.role === roleFilter;
      const schoolMatch = schoolFilter === 'all' || user.school === schoolFilter;
      
      return roleMatch && schoolMatch;
    });
  }, [data, roleFilter, schoolFilter]);

  const tableInstance = useTable(
    { columns, data: filteredData, initialState: { pageIndex: 0, pageSize: 10 } }, // Use filtered data
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    /* table + filter helpers */
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    /* pagination helpers */
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    /* state */
    state: { pageIndex, globalFilter },
  } = tableInstance;

  /* ────────────────────── Robust Checks & Logging - AFTER HOOKS ARE CALLED ────────────────────── */
  // These checks are still useful for providing clear feedback in the UI.
  if (!columns || columns.length === 0) {
    console.error("ManagementTable: Invalid or empty 'columnsData' prop. Please provide an array of column definitions.");
    return (
      <Card extra="w-full px-9 py-3 h-full flex items-center justify-center">
        <p className="py-8 text-center text-red-500 dark:text-red-400">
          Error: Column definitions are missing.
        </p>
      </Card>
    );
  }
  
  /* ────────────────────── UI handlers ────────────────────── */
  const handleEditUser = (user) => setEditUser(user);
  const closeEditModal = () => setEditUser(null);

  const defaultImageUrl =
    "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";

  /* ────────────────────────────── JSX ───────────────────────────── */
  return (
    <Card extra="w-full px-6 py-5 h-full shadow-lg rounded-[20px] bg-white dark:!bg-navy-800">
      {/* Header */}
      <div className="flex w-full items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-orange-400 to-orange-600">
            <MdEdit className="h-6 w-6 text-white" />
          </div>
          <div>
            <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
              User Management
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Manage your platform users and their details
            </p>
          </div>
        </div>
        <div className="rounded-xl bg-gradient-to-r from-orange-400 to-orange-600 px-4 py-2">
          <span className="text-sm font-semibold text-white">
            {filteredData.length === data.length ? data.length : `${filteredData.length}/${data.length}`} Users
          </span>
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-6 flex w-full max-w-md items-center rounded-xl bg-gray-50 p-3 shadow-sm transition-all duration-200 hover:shadow-md dark:!bg-navy-900">
        <FiSearch className="h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search users by name, email, school, team..."
          value={globalFilter || ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="block w-full bg-transparent pl-3 text-sm text-navy-700 outline-none placeholder:text-gray-400 dark:text-white dark:placeholder:text-gray-500"
        />
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        {/* Role Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Role:</label>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
          >
            <option value="all">All Roles</option>
            <option value="user">Users</option>
            <option value="mentor">Mentors</option>
            <option value="admin">Admins</option>
          </select>
        </div>

        {/* School Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">School:</label>
          <select
            value={schoolFilter}
            onChange={(e) => setSchoolFilter(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
          >
            <option value="all">All Schools</option>
            {[...new Set(data.map(u => u.school).filter(Boolean))].sort().map(school => (
              <option key={school} value={school}>
                {school.includes('Kenya') ? '🇰🇪 ' : school.includes('Somalia') || school.includes('SIMAD') ? '🇸🇴 ' : '🌍 '}
                {school}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Filters Button */}
        {(roleFilter !== 'all' || schoolFilter !== 'all') && (
          <button
            onClick={() => {
              setRoleFilter('all');
              setSchoolFilter('all');
            }}
            className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 transition-colors duration-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
          >
            Clear Filters
          </button>
        )}

        {/* Results Count */}
        <div className="ml-auto text-sm text-gray-500 dark:text-gray-400">
          Showing {filteredData.length} of {data.length} users
        </div>
      </div>

      {/* Active Filters Summary */}
      {(roleFilter !== 'all' || schoolFilter !== 'all') && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Active filters:</span>
          {roleFilter !== 'all' && (
            <span className="inline-flex items-center rounded-full bg-brand-100 px-3 py-1 text-xs font-medium text-brand-800 dark:bg-brand-900/30 dark:text-brand-200">
              Role: {roleFilter.charAt(0).toUpperCase() + roleFilter.slice(1)}
              <button
                onClick={() => setRoleFilter('all')}
                className="ml-2 text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-200"
              >
                ×
              </button>
            </span>
          )}
          {schoolFilter !== 'all' && (
            <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
              School: {schoolFilter}
              <button
                onClick={() => setSchoolFilter('all')}
                className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
              >
                ×
              </button>
            </span>
          )}
        </div>
      )}

      {/* Conditional Rendering for Empty Data */}
      {filteredData.length === 0 ? (
        <div className="flex items-center justify-center w-full h-48">
          {data.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400">
              No users to display.
            </p>
          ) : (
            <div className="text-center">
              <p className="text-gray-500 dark:text-gray-400 mb-2">
                No users match the selected filters.
              </p>
              <button
                onClick={() => {
                  setRoleFilter('all');
                  setSchoolFilter('all');
                }}
                className="text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300 font-medium"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto rounded-xl bg-white dark:bg-navy-700 shadow-sm">
            <table {...getTableProps()} className="w-full font-dm text-navy-700">
              <thead className="bg-gray-50 dark:bg-navy-800">
                {headerGroups.map((headerGroup, index) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={index}
                    className="items-center border-b border-gray-100 dark:border-gray-600"
                  >
                    {headerGroup.headers.map((column, colIndex) => (
                      <th
                        key={colIndex}
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className="px-6 py-4 text-start text-xs font-bold uppercase tracking-wider text-gray-500 cursor-pointer hover:text-orange-500 transition-colors duration-200 dark:text-gray-400 dark:hover:text-orange-400"
                      >
                        <div className="flex items-center gap-2">
                          {column.render("Header")}
                          {column.isSorted && (
                            <span className="text-sm text-orange-500">
                              {column.isSortedDesc ? '↓' : '↑'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {page.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      key={rowIndex}
                      className="border-b border-gray-50 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-navy-600 transition-colors duration-200"
                    >
                      {row.cells.map((cell, cellIndex) => {
                        let content;

                        /* Name (with avatar) */
                        if (cell.column.Header === "Name") {
                          const imageUrl = cell.value?.[2] 
                            ? `${process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") || ""}/uploads/images/${cell.value[2].split('/').pop()}`
                            : defaultImageUrl;
                          content = (
                            <div className="flex items-center gap-4 pr-4 sm:min-w-[180px]">
                              <div className="relative">
                                <img
                                  src={imageUrl}
                                  alt="avatar"
                                  className="h-12 w-12 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-600"
                                  onError={(e) => { e.target.onerror = null; e.target.src = defaultImageUrl; }}
                                />
                                <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-green-400 ring-2 ring-white dark:ring-navy-700"></div>
                              </div>
                              <div className="flex flex-col">
                                <p className="text-sm font-semibold text-navy-700 dark:text-white">
                                  {cell.value?.[0] || 'N/A'}
                                </p>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                  @{cell.value?.[1] || 'N/A'}
                                </p>
                              </div>
                            </div>
                          );
                        }

                        /* Phone */
                        else if (cell.column.Header === "Phone") {
                          const isMissing = !cell.value;
                          content = (
                            <div className="flex items-center">
                              {isMissing ? (
                                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-600 dark:bg-red-500/20 dark:text-red-400">
                                  No Phone
                                </span>
                              ) : (
                                <p className="text-sm font-medium text-navy-700 dark:text-white">
                                  {cell.value}
                                </p>
                              )}
                            </div>
                          );
                        }

                        /* Team / Role / School */
                        else if (["Team", "Role", "School"].includes(cell.column.Header)) {
                          const isNoTeam = cell.column.Header === "Team" && (!cell.value || cell.value === "No Team");
                          content = (
                            <div className="flex items-center">
                              {isNoTeam ? (
                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-500/20 dark:text-gray-400">
                                  No Team
                                </span>
                              ) : (
                                <p className="text-sm font-semibold text-navy-700 dark:text-white">
                                  {cell.value || 'N/A'}
                                </p>
                              )}
                            </div>
                          );
                        }

                        /* Member Since */
                        else if (cell.column.Header === "Member Since") {
                          let formattedDate = 'N/A';
                          let relativeTime = '';
                          try {
                            if (cell.value) {
                              const date = new Date(cell.value);
                              formattedDate = date.toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              });
                              
                              // Calculate relative time
                              const now = new Date();
                              const diffTime = Math.abs(now - date);
                              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                              
                              if (diffDays < 30) {
                                relativeTime = `${diffDays} days ago`;
                              } else if (diffDays < 365) {
                                const months = Math.floor(diffDays / 30);
                                relativeTime = `${months} month${months > 1 ? 's' : ''} ago`;
                              } else {
                                const years = Math.floor(diffDays / 365);
                                relativeTime = `${years} year${years > 1 ? 's' : ''} ago`;
                              }
                            }
                          } catch (e) {
                            console.error("ManagementTable: Error parsing date for 'Member Since'. Value:", cell.value, e);
                            formattedDate = 'Invalid Date';
                          }
                          content = (
                            <div className="flex flex-col">
                              <p className="text-sm font-semibold text-navy-700 dark:text-white">
                                {formattedDate}
                              </p>
                              {relativeTime && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {relativeTime}
                                </p>
                              )}
                            </div>
                          );
                        }

                        /* Status badge */
                        else if (cell.column.Header === "Status") {
                          const statusValue = cell.value ? String(cell.value).toLowerCase() : '';
                          const getStatusStyle = (status) => {
                            switch (status) {
                              case 'online':
                                return "bg-green-100 text-green-600 ring-1 ring-green-500/20 dark:bg-green-500/20 dark:text-green-400";
                              case 'busy':
                                return "bg-orange-100 text-orange-600 ring-1 ring-orange-500/20 dark:bg-orange-500/20 dark:text-orange-400";
                              case 'away':
                                return "bg-yellow-100 text-yellow-600 ring-1 ring-yellow-500/20 dark:bg-yellow-500/20 dark:text-yellow-400";
                              case 'offline':
                              default:
                                return "bg-gray-100 text-gray-600 ring-1 ring-gray-500/20 dark:bg-gray-500/20 dark:text-gray-400";
                            }
                          };
                          
                          content = (
                            <div className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${getStatusStyle(statusValue)}`}>
                              <div className={`mr-1.5 h-2 w-2 rounded-full ${
                                statusValue === 'online' ? 'bg-green-400' :
                                statusValue === 'busy' ? 'bg-orange-400' :
                                statusValue === 'away' ? 'bg-yellow-400' : 'bg-gray-400'
                              }`}></div>
                              {cell.value || 'Offline'}
                            </div>
                          );
                        }

                        /* Edit button */
                        else if (cell.column.Header === "") {
                          content = (
                            <button
                              onClick={() => handleEditUser(row.original)}
                              className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-white hover:bg-orange-500 rounded-lg transition-all duration-200 dark:text-gray-500 dark:hover:bg-orange-500"
                              title="Edit User"
                            >
                              <MdEdit className="h-4 w-4" />
                            </button>
                          );
                        } else {
                          // Default rendering for any other column
                          content = (
                            <p className="text-sm font-semibold text-navy-700 dark:text-white">
                              {cell.value || 'N/A'}
                            </p>
                          );
                        }

                        return (
                          <td
                            {...cell.getCellProps()}
                            key={cellIndex}
                            className="px-6 py-4 whitespace-nowrap"
                          >
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-6 flex items-center justify-between bg-gray-50 dark:bg-navy-800 px-6 py-4 rounded-b-xl">
            <div className="flex items-center">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Showing <span className="font-semibold">{pageIndex * 10 + 1}</span> to{' '}
                <span className="font-semibold">
                  {Math.min((pageIndex + 1) * 10, data.length)}
                </span>{' '}
                of <span className="font-semibold">{data.length}</span> users
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={previousPage}
                disabled={!canPreviousPage}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed dark:bg-navy-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-navy-600 dark:disabled:bg-navy-800 dark:disabled:text-gray-500 transition-colors duration-200"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Page <span className="font-semibold">{pageIndex + 1}</span> of{' '}
                <span className="font-semibold">{pageOptions.length}</span>
              </span>
              <button
                onClick={nextPage}
                disabled={!canNextPage}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed dark:bg-navy-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-navy-600 dark:disabled:bg-navy-800 dark:disabled:text-gray-500 transition-colors duration-200"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Edit modal */}
      {editUser && <EditUserModal user={editUser} onClose={closeEditModal} />}
    </Card>
  );
};

export default ManagementTable;
